import React, { useState } from "react";
import { FileUpload, DeleteIcon } from "../../assets/svg";
import { toast } from "react-toastify";

const FileUploadChoose = ({
  handleFileChange,
  className,
  uploadedFiles = [],
  onDelete,
  disabled,
  uploadIcon,
  uploadText,
  deleteIcon,
  accept,
  videoOrientation = "landscape", // 'landscape' or 'portrait'
  filemsg = "jpeg, jpg, png, mp4, mov (max. 200mb)",
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const isVideoFile = (fileObj) => {
    if (!fileObj) return false;
    if (fileObj.type && typeof fileObj.type === "string") {
      return fileObj.type.startsWith("video/");
    }
    if (fileObj.file && fileObj.file.type) {
      return fileObj.file.type.startsWith("video/");
    }
    const url = fileObj.url || "";
    return /\.(mp4|mov)(\?.*)?$/i.test(url);
  };

  const getFileSrc = (fileObj) => {
    if (!fileObj) return "";
    if (fileObj.file instanceof Blob) {
      return URL.createObjectURL(fileObj.file);
    }
    return fileObj.url || "";
  };

  const onFileChange = async (event) => {
    setIsUploading(true);
    try {
      await handleFileChange(event);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    // Filter files based on accept prop
    let allowedFiles = files;
    if (accept === "image/*") {
      allowedFiles = files.filter((file) => file.type.startsWith("image/"));
    } else if (accept === "video/*") {
      allowedFiles = files.filter((file) => file.type.startsWith("video/"));
    } else if (accept) {
      // if specific extensions like .jpg,.png
      const acceptedTypes = accept
        .split(",")
        .map((t) => t.trim().toLowerCase());
      allowedFiles = files.filter((file) =>
        acceptedTypes.some((type) => file.name.toLowerCase().endsWith(type))
      );
    }

    if (allowedFiles.length === 0) {
      toast.error("Only image files are allowed.");
      return;
    }

    const syntheticEvent = {
      target: { files: allowedFiles },
    };

    await onFileChange(syntheticEvent);
  };

  const handlePreview = (fileObj) => {
    setSelectedFile(fileObj);
  };

  const hasFiles = Array.isArray(uploadedFiles) && uploadedFiles.length > 0;

  return (
    <div
      className={`relative flex flex-col items-center justify-center ${
        hasFiles
          ? ""
          : `border ${
              !disabled
                ? "border-dashed cursor-pointer border-borderDot"
                : "border-gray-300 flex justify-start cursor-not-allowed border-borderDot"
            }`
      } rounded-2xl overflow-hidden px-4 py-8 ${className} ${
        isDragOver && !disabled
          ? `${hasFiles ? "bg-blue-50" : "border-blue-400 bg-blue-50"}`
          : ""
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag Overlay */}
      {isDragOver && !disabled && (
        <div className="absolute inset-0 bg-blue-50/80 flex items-center justify-center z-10 rounded-2xl">
          <div className="text-center">
            <div className="text-blue-600 text-lg font-medium mb-2">
              Drop files here
            </div>
            <div className="text-blue-500 text-sm">Release to upload</div>
          </div>
        </div>
      )}

      {/* Loader Overlay */}
      {isUploading && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-20">
          <div className="w-12 h-12 border-4 border-themeBtn border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <div className="flex flex-wrap">
          {uploadedFiles.map((file, index) => (
            <div key={index} className="relative preview-container m-2">
              {isVideoFile(file) ? (
                <video
                  controls
                  src={getFileSrc(file)}
                  className={`rounded-lg object-cover ${
                    videoOrientation === "portrait"
                      ? "w-64 h-96" // Portrait preview
                      : "w-[880px] h-96" // Landscape preview
                  }`}
                  style={{ objectFit: "cover" }}
                  onClick={() => handlePreview(file)} // opens fullscreen preview
                />
              ) : (
                <img
                  src={getFileSrc(file)}
                  alt="preview"
                  className="w-40 h-40 rounded-lg object-cover"
                  onClick={() => handlePreview(file)} // opens fullscreen preview
                />
              )}

              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDelete?.(index);
                  }}
                  className="absolute top-1 right-1 w-7 h-7 flex items-center justify-center
              bg-white text-red-500 rounded-full shadow-md hover:bg-red-500 hover:text-white
              transition-all duration-200 z-20"
                >
                  {deleteIcon || <DeleteIcon className="w-4 h-4" />}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload Area (clickable) */}
      {/* Upload Area (clickable) */}
      {!disabled && uploadedFiles.length === 0 && (
        <label className="mt-4 flex flex-col items-center cursor-pointer">
          {uploadIcon || <FileUpload width={60} height={60} className="mb-4" />}
          {uploadText || (
            <>
              <p className="text-base mb-2 font-Proxima">
                Drag and Drop or{" "}
                <span className="text-[#0288d1] font-Proxima">
                  choose your file
                </span>{" "}
                for upload
              </p>
              <p className="text-textSecondary text-base font-Proxima">
                {filemsg}
              </p>
            </>
          )}
          <input
            type="file"
            onChange={onFileChange}
            accept={accept || "image/*,video/*"}
            disabled={disabled}
            multiple
            className="hidden"
          />
        </label>
      )}
    </div>
  );
};

export default FileUploadChoose;
